import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { projects as projectsApi, payments as paymentsApi } from "@/lib/api";
import { X, Copy, Loader2, RefreshCw, Share2, Sparkles, Wand2 } from "lucide-react";

export function DeckActionsModal({ deck, onClose, onUpdate }) {
  const [tab, setTab] = useState("share");
  const [pw, setPw] = useState(deck.share_password || "");
  const [enabled, setEnabled] = useState(!!deck.share_token);
  const [token, setToken] = useState(deck.share_token || "");
  const [slug, setSlug] = useState(deck.custom_slug || "");
  const [savedSlug, setSavedSlug] = useState(deck.custom_slug || "");
  const [busy, setBusy] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [selPage, setSelPage] = useState(deck.pages?.[0]?.id || "");
  const [status, setStatus] = useState(deck.status);

  const shareId = savedSlug || token;
  const shareUrl = shareId ? `${window.location.origin}/deck/${shareId}${pw ? `?pw=${encodeURIComponent(pw)}` : ""}` : "";

  useEffect(() => {
    if (status !== "generating") return;
    const t = setInterval(async () => {
      try {
        const s = await projectsApi.status(deck.id);
        setStatus(s.status);
        if (s.status !== "generating") { clearInterval(t); onUpdate?.(); }
      } catch { clearInterval(t); }
    }, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [status]);

  const saveShare = async () => {
    setBusy(true);
    try {
      const r = await projectsApi.share(deck.id, enabled, pw || null, slug);
      setToken(r.token || "");
      setSavedSlug(r.custom_slug || "");
      toast.success(enabled ? "Share link updated" : "Share link disabled");
      onUpdate?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not update share";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const buyRevision = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await paymentsApi.checkout("revision", deck.id, 1);
      window.location.href = checkout_url;
    } catch { toast.error("Could not open checkout"); setBusy(false); }
  };

  const runRevision = async () => {
    setBusy(true);
    try {
      await projectsApi.revise(deck.id, selPage, instructions);
      setStatus("generating");
      toast.success("Regenerating page…");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("No revision credits — head to checkout");
      } else {
        toast.error("Revision failed to start");
      }
    } finally { setBusy(false); }
  };

  const buyPremiumGraphics = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await paymentsApi.checkout("premium_graphics", deck.id, 1);
      window.location.href = checkout_url;
    } catch { toast.error("Could not open checkout"); setBusy(false); }
  };

  const runGraphics = async () => {
    setBusy(true);
    try {
      await projectsApi.graphics(deck.id);
      setStatus("generating");
      toast.success("Generating premium background…");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Purchase premium graphics first");
      else toast.error("Could not generate");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Copied link");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="kd-glass-strong rounded-3xl p-8 w-full max-w-lg relative"
          data-testid="deck-actions-modal"
        >
          <button onClick={onClose} data-testid="deck-actions-close" className="absolute top-4 right-4 rounded-full p-2 bg-white/5 hover:bg-white/10 border border-white/10">
            <X size={14} />
          </button>
          <div className="kd-tag mb-3">Deck actions</div>
          <h2 className="kd-display text-2xl mb-1">{deck.info?.stage_name || "Untitled"}</h2>
          <p className="text-zinc-500 text-xs mb-6">{deck.pages?.length || 0} pages · {deck.status}</p>

          <div className="flex gap-2 mb-6">
            {["share", "revise", "graphics"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`deck-tab-${t}`}
                className={`rounded-full px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase transition-all ${
                  tab === t ? "bg-[#E50914] text-white" : "bg-white/[0.03] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.06]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === "share" && (
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="share-enabled" className="accent-[#E50914]" />
                <span className="text-white/90 text-sm">Enable public share link</span>
              </label>
              <div>
                <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">Custom URL (optional)</div>
                <div className="flex items-stretch bg-black/40 border border-white/10 rounded-xl overflow-hidden focus-within:border-white/30">
                  <div className="px-3 py-3 text-zinc-500 text-xs bg-black/40 border-r border-white/10 whitespace-nowrap">
                    {window.location.host}/deck/
                  </div>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase())}
                    data-testid="share-slug"
                    placeholder="yourname"
                    className="flex-1 bg-transparent px-3 py-3 text-white text-sm outline-none placeholder:text-zinc-600"
                  />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Lowercase letters, digits and dashes. 2–40 characters.</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">Optional password</div>
                <input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="share-password" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30" placeholder="Leave blank for open link" />
              </div>
              <button onClick={saveShare} disabled={busy} data-testid="share-save" className="w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Save link
              </button>
              {shareId && enabled && (
                <div className="rounded-xl p-3 bg-black/40 border border-white/[0.08] flex items-center gap-2">
                  <input readOnly value={shareUrl} data-testid="share-url" className="flex-1 bg-transparent text-xs text-zinc-300 outline-none truncate" />
                  <button onClick={copy} data-testid="share-copy" className="rounded-full p-2 bg-white/5 hover:bg-white/10 border border-white/10">
                    <Copy size={12} />
                  </button>
                </div>
              )}
              {token && (
                <div className="text-[11px] text-zinc-500 flex items-center gap-4">
                  <span>Views: <span className="text-white/80">{deck.share_views || 0}</span></span>
                  <span>Downloads: <span className="text-white/80">{deck.share_downloads || 0}</span></span>
                </div>
              )}
            </div>
          )}

          {tab === "revise" && (
            <div className="space-y-4">
              <div>
                <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">Page to regenerate</div>
                <select value={selPage} onChange={(e) => setSelPage(e.target.value)} data-testid="revise-page" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30">
                  {(deck.pages || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">Instructions (optional)</div>
                <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="revise-instructions" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30" placeholder="e.g. Make it more focused on brand sponsorships" />
              </div>
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span>Credits available: <span className="text-white/80">{deck.revisions_available || 0}</span></span>
              </div>
              {(deck.revisions_available || 0) > 0 ? (
                <button onClick={runRevision} disabled={busy || status === "generating"} data-testid="revise-run" className="w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white flex items-center justify-center gap-2 disabled:opacity-50">
                  {status === "generating" ? <><Loader2 size={14} className="animate-spin" /> Regenerating…</> : <><RefreshCw size={14} /> Regenerate this page</>}
                </button>
              ) : (
                <button onClick={buyRevision} disabled={busy} data-testid="revise-buy" className="w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white flex items-center justify-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Buy revision · $1.99
                </button>
              )}
            </div>
          )}

          {tab === "graphics" && (
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm leading-relaxed">
                Premium Graphics uses <span className="text-white">Gemini Nano Banana</span> to craft a cinematic, bespoke background image that layers behind every page of your deck.
              </p>
              <div className="rounded-xl p-4 bg-black/30 border border-white/[0.08] text-xs text-zinc-400 flex items-center justify-between">
                <span>Status</span>
                <span className="text-white/80">{deck.premium_graphics ? "Enabled" : "Not purchased"}</span>
              </div>
              {deck.premium_graphics ? (
                <button onClick={runGraphics} disabled={busy || status === "generating"} data-testid="graphics-run" className="w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white flex items-center justify-center gap-2 disabled:opacity-50">
                  {status === "generating" ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate new background</>}
                </button>
              ) : (
                <button onClick={buyPremiumGraphics} disabled={busy} data-testid="graphics-buy" className="w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white flex items-center justify-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Unlock Premium Graphics · $4.99
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
